import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  Observable,
} from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  setTokens,
} from '../utils/auth'
import {
  RefreshAccessTokenDocument,
  RefreshAccessTokenMutation,
  RefreshAccessTokenMutationVariables,
} from './generated/graphql'
import { createTaggedLogger } from '../utils/logger'
import { errorReporter } from '../utils/errorReporter'

// Auth 관련 정상 흐름 에러 — errorReporter 전송에서 제외 (noise 방지).
// authErrorLink / refreshTokenLink가 이미 로그인 리다이렉트·토큰 갱신으로 처리.
const AUTH_ERROR_CODES = new Set([
  'UNAUTHENTICATED',
  'TOKEN_EXPIRED',
  'INVALID_TOKEN',
])

// 리포트 요청 자체는 순환 방지를 위해 항상 제외.
// errorReporter는 토큰 유무에 따라 두 뮤테이션 중 하나를 호출하므로 둘 다 등록.
const REPORT_OPERATION_NAMES = new Set([
  'ReportClientErrors',
  'ReportAnonymousClientErrors',
])

const authLinkLogger = createTaggedLogger('AuthLink')
const authErrorLinkLogger = createTaggedLogger('AuthErrorLink')
const refreshTokenLinkLogger = createTaggedLogger('RefreshTokenLink')
const errorLinkLogger = createTaggedLogger('ErrorLink')

// GraphQL 에러의 originalError 타입 정의
interface OriginalError {
  statusCode: number // HTTP 상태 코드 (401, 403, 등)
  message: string // 에러 메시지
  error: string // 에러 이름 ('Unauthorized', 'Forbidden' 등)
  code: string // 커스텀 에러 코드 ('UNAUTHORIZED', 'TOKEN_EXPIRED', 'INVALID_TOKEN' 등)
}

// GraphQL 에러 확장 타입 정의
interface GraphQLErrorExtensions {
  code?: string
  originalError?: OriginalError
  [key: string]: any
}

const httpLink = new HttpLink({
  uri: process.env.REACT_APP_GRAPHQL_URI || 'http://localhost:3100/graphql',
  credentials: 'include',
})

const authLink = setContext((operation, { headers }) => {
  // Always read fresh token from sessionStorage
  const token = getAccessToken()

  // Enhanced debug logging
  const now = Date.now()
  const timeSinceRefresh = now - lastRefreshTime

  if (isRefreshing || timeSinceRefresh < 1000) {
    authLinkLogger.debug('Token state:', {
      operation: operation.operationName,
      hasToken: !!token,
      tokenLength: token?.length || 0,
      isRefreshing,
      timeSinceRefresh,
    })
  }

  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
      'content-type': 'application/json',
    },
  }
})

// Store refresh promise to prevent duplicate refreshes
let refreshPromise: Promise<any> | null = null

// Global flag to track if token refresh is in progress
let isRefreshing = false

// Track last refresh time for grace period
let lastRefreshTime = 0
const REFRESH_GRACE_PERIOD = 2000 // 2 seconds grace period after refresh

// 인증 흐름 작업: "unauthorized"(잘못된 비밀번호/코드)가 정상적으로 발생하며,
// 컴포넌트가 인라인 에러로 보여줘야 한다. 전역 로그아웃-리다이렉트에서 제외.
const AUTH_FLOW_OPERATIONS = new Set([
  'SignIn',
  'VerifyLoginTwoFactor',
  'VerifyLoginDevice',
  'ResendLoginDeviceCode',
  'ConfirmTwoFactorEnrollment',
])

// Authentication Error Link - handles UNAUTHENTICATED errors
const authErrorLink = new ApolloLink((operation, forward) => {
  // 인증 흐름 작업은 전역 리다이렉트 처리에서 제외 (인라인 에러로 표시)
  if (AUTH_FLOW_OPERATIONS.has(operation.operationName ?? '')) {
    return forward(operation)
  }

  return new Observable((observer) => {
    const subscription = forward(operation).subscribe({
      next: (response) => {
        // Forced 2FA enrollment guard: 서버가 미등록 사용자의 모든 op 를 차단할 때
        // 발생하는 에러를 감지해 setup 페이지로 보낸다 (이미 setup 페이지면 제외).
        if (response?.errors && response.errors.length > 0) {
          const enrollmentRequiredError = response.errors.find(
            (err: any) =>
              err?.extensions?.originalError?.code ===
                'TWO_FACTOR_ENROLLMENT_REQUIRED' ||
              (err?.message as string | undefined)?.includes(
                'Two-factor authentication setup is required'
              )
          )
          if (
            enrollmentRequiredError &&
            window.location.pathname !== '/security/2fa-setup'
          ) {
            window.location.href = '/security/2fa-setup'
          }
        }

        // Check for authentication errors in the response
        if (response?.errors && response.errors.length > 0) {
          const authError = response.errors.find((err: any) => {
            const extensions = err.extensions as GraphQLErrorExtensions
            const code = extensions?.code
            const originalCode = extensions?.originalError?.code

            // Check for UNAUTHENTICATED + UNAUTHORIZED (no token provided)
            return code === 'UNAUTHENTICATED' && originalCode === 'UNAUTHORIZED'
          })

          if (authError) {
            const now = Date.now()
            const isInGracePeriod = now - lastRefreshTime < REFRESH_GRACE_PERIOD
            const hasToken = !!getAccessToken()
            const timestamp = new Date().toISOString()
            const extensions = authError.extensions as GraphQLErrorExtensions

            // ALWAYS log UNAUTHORIZED errors with FULL server response
            authErrorLinkLogger.error('UNAUTHORIZED ERROR DETECTED', {
              operation: operation.operationName,
              errorCode: extensions?.code,
              originalErrorCode: extensions?.originalError?.code,
              originalErrorStatusCode: extensions?.originalError?.statusCode,
              originalErrorMessage: extensions?.originalError?.message,
              isRefreshing,
              isInGracePeriod,
              hasToken,
              willRedirect: !isRefreshing && !isInGracePeriod,
            })

            // Clear tokens and redirect to login if:
            // 1. Not currently refreshing
            // 2. Not in grace period after refresh
            if (!isRefreshing && !isInGracePeriod) {
              clearTokens()
              window.location.href = '/login'
            }
            // Still pass the response to prevent breaking the chain
          }
        }
        observer.next(response)
      },
      error: (error) => observer.error(error),
      complete: () => observer.complete(),
    })

    // Return cleanup function
    return () => {
      if (subscription) {
        subscription.unsubscribe()
      }
    }
  })
})

// Token Refresh Link - handles TOKEN_EXPIRED errors only
const refreshTokenLink = new ApolloLink((operation, forward) => {
  // Skip refresh for the refresh mutation itself and SignIn mutation
  if (
    operation.operationName === 'RefreshAccessToken' ||
    operation.operationName === 'SignIn'
  ) {
    return forward(operation)
  }

  return new Observable((observer) => {
    let subscription: any = null
    let isHandlingTokenRefresh = false

    subscription = forward(operation).subscribe({
      next: (data) => {
        // Check for TOKEN_EXPIRED errors in the response data
        if (data?.errors && data.errors.length > 0) {
          const tokenError = data.errors.find((err: any) => {
            const extensions = err.extensions as GraphQLErrorExtensions
            const code = extensions?.code
            const originalCode = extensions?.originalError?.code

            // Only handle TOKEN_EXPIRED and INVALID_TOKEN
            return (
              code === 'TOKEN_EXPIRED' ||
              originalCode === 'TOKEN_EXPIRED' ||
              (code === 'UNAUTHENTICATED' &&
                originalCode === 'TOKEN_EXPIRED') ||
              code === 'INVALID_TOKEN' ||
              originalCode === 'INVALID_TOKEN'
            )
          })

          if (tokenError) {
            const extensions = tokenError.extensions as GraphQLErrorExtensions
            const code = extensions?.code
            const originalCode = extensions?.originalError?.code

            // Handle INVALID_TOKEN - immediate logout
            if (code === 'INVALID_TOKEN' || originalCode === 'INVALID_TOKEN') {
              clearTokens()
              window.location.href = '/login'
              return
            }

            // Handle TOKEN_EXPIRED - try to refresh
            isHandlingTokenRefresh = true
            // Unsubscribe from the original subscription to prevent complete event
            if (subscription) {
              subscription.unsubscribe()
            }
            // Handle token refresh
            handleTokenRefresh(operation, forward, observer, tokenError)
            return
          }
        }
        observer.next(data)
      },
      error: (error) => {
        // Handle network errors that might contain token expiration
        isHandlingTokenRefresh = true
        handleTokenRefresh(operation, forward, observer, error)
      },
      complete: () => {
        // Only complete if not handling token refresh
        if (!isHandlingTokenRefresh) {
          observer.complete()
        }
      },
    })

    // Return unsubscribe function
    return () => {
      if (subscription) {
        subscription.unsubscribe()
      }
    }
  })
})

// Helper function to handle token refresh
async function handleTokenRefresh(
  operation: any,
  forward: any,
  observer: any,
  error: any
) {
  // Check for different auth error types
  let errorCode: string | null = null
  let shouldRefresh = false

  // Check if error is a single GraphQL error object (from response data)
  if (error.extensions?.code || error.extensions?.originalError?.code) {
    const extensions = error.extensions as GraphQLErrorExtensions
    const code = extensions?.code
    const originalCode = extensions?.originalError?.code

    if (code === 'TOKEN_EXPIRED' || originalCode === 'TOKEN_EXPIRED') {
      errorCode = 'TOKEN_EXPIRED'
      shouldRefresh = true
    } else if (code === 'UNAUTHENTICATED' && originalCode === 'TOKEN_EXPIRED') {
      errorCode = 'TOKEN_EXPIRED'
      shouldRefresh = true
    }
  }

  // Check GraphQL errors array
  if (!errorCode && error.graphQLErrors?.length > 0) {
    for (const err of error.graphQLErrors) {
      // Check error code and originalError code
      const extensions = err.extensions as GraphQLErrorExtensions
      const code = extensions?.code
      const originalCode = extensions?.originalError?.code

      if (code === 'TOKEN_EXPIRED' || originalCode === 'TOKEN_EXPIRED') {
        errorCode = 'TOKEN_EXPIRED'
        shouldRefresh = true
        break
      } else if (
        code === 'UNAUTHENTICATED' &&
        originalCode === 'TOKEN_EXPIRED'
      ) {
        errorCode = 'TOKEN_EXPIRED'
        shouldRefresh = true
        break
      }
    }
  }

  // Check network errors (HTTP 401 responses)
  if (!errorCode && error.networkError) {
    const networkError = error.networkError as any

    if (networkError.statusCode === 401) {
      if (networkError.result?.code === 'TOKEN_EXPIRED') {
        errorCode = 'TOKEN_EXPIRED'
        shouldRefresh = true
      } else {
        // For other 401 errors, don't assume token refresh
        // Let authErrorLink handle it
        observer.error(error)
        return
      }
    }
  }

  // Handle TOKEN_EXPIRED - try refresh
  if (shouldRefresh || errorCode === 'TOKEN_EXPIRED') {
    const refreshToken = getRefreshToken()

    if (refreshToken) {
      try {
        let newToken: string

        // Use existing refresh promise if available
        if (refreshPromise) {
          // Wait for the existing refresh to complete
          newToken = await refreshPromise

          refreshTokenLinkLogger.debug(
            'Using existing refresh promise for:',
            operation.operationName
          )
        } else {
          // Mark refresh as starting immediately
          isRefreshing = true

          refreshPromise = (async () => {
            // Create a separate client for refresh token to avoid circular dependency
            const refreshLink = setContext((_, { headers }) => ({
              headers: {
                ...headers,
                'content-type': 'application/json',
              },
            }))

            const refreshClient = new ApolloClient({
              link: ApolloLink.from([refreshLink, httpLink]),
              cache: new InMemoryCache(),
            })

            // Try to refresh the access token
            const { data } = await refreshClient.mutate<
              RefreshAccessTokenMutation,
              RefreshAccessTokenMutationVariables
            >({
              mutation: RefreshAccessTokenDocument,
              variables: { input: { refreshToken } },
            })

            if (
              data?.refreshAccessToken?.accessToken &&
              data.refreshAccessToken.refreshToken
            ) {
              // Update tokens
              setTokens(
                data.refreshAccessToken.accessToken,
                data.refreshAccessToken.refreshToken
              )

              // Update last refresh time
              lastRefreshTime = Date.now()

              // Debug logging
              refreshTokenLinkLogger.debug('Token refresh successful')
              const savedToken = getAccessToken()
              refreshTokenLinkLogger.debug(
                'Token saved check:',
                savedToken === data.refreshAccessToken.accessToken
              )

              return data.refreshAccessToken.accessToken
            } else {
              throw new Error('Refresh failed')
            }
          })()

          // First request: wait for refresh and get the token
          newToken = await refreshPromise

          // Delay clearing refreshPromise to allow concurrent requests to complete
          setTimeout(() => {
            refreshPromise = null
            isRefreshing = false
            refreshTokenLinkLogger.debug('Refresh state cleared after delay')
          }, 100) // 100ms delay
        }

        // Common retry logic for all requests (both first and concurrent)
        // Verify token was saved correctly with retry logic
        let retryCount = 0
        const maxRetries = 3
        while (retryCount < maxRetries) {
          const savedToken = getAccessToken()
          if (savedToken === newToken) {
            break // Token saved successfully
          }

          refreshTokenLinkLogger.warn(
            `Token not saved correctly, retry ${retryCount + 1}/${maxRetries}`
          )

          // Try to set it again
          const currentRefreshToken = getRefreshToken()
          if (currentRefreshToken) {
            setTokens(newToken, currentRefreshToken)
          }

          // Wait a bit before checking again
          await new Promise((resolve) => setTimeout(resolve, 50))
          retryCount++
        }

        // Add small delay to ensure token is properly propagated
        await new Promise((resolve) => setTimeout(resolve, 20))

        // Debug logging before retry
        const retryToken = getAccessToken()
        refreshTokenLinkLogger.debug(
          'Before retry - Token present:',
          !!retryToken
        )
        refreshTokenLinkLogger.debug(
          'Retrying operation:',
          operation.operationName
        )

        // Update the operation context with the new token
        const oldHeaders = operation.getContext().headers
        operation.setContext({
          headers: {
            ...oldHeaders,
            authorization: `Bearer ${newToken}`,
          },
        })

        // Retry the request with updated context
        forward(operation).subscribe({
          next: (data: any) => {
            refreshTokenLinkLogger.debug(
              'Retry successful for:',
              operation.operationName
            )
            observer.next(data)
          },
          error: (err: any) => {
            refreshTokenLinkLogger.error(
              'Retry failed for:',
              operation.operationName,
              err
            )
            observer.error(err)
          },
          complete: () => observer.complete(),
        })

        return // Exit after retrying
      } catch (refreshError) {
        // Refresh token is invalid or expired
        refreshTokenLinkLogger.error('REFRESH FAILED', {
          refreshError: JSON.stringify(refreshError, null, 2),
          originalError: JSON.stringify(error, null, 2),
        })

        // Clear state with delay to prevent race conditions
        setTimeout(() => {
          refreshPromise = null
          isRefreshing = false
        }, 100)

        clearTokens()
        window.location.href = '/login'
        observer.error(error)
      }
    } else {
      // No refresh token available
      refreshTokenLinkLogger.error('NO REFRESH TOKEN AVAILABLE', {
        originalError: JSON.stringify(error, null, 2),
      })
      isRefreshing = false
      clearTokens()
      window.location.href = '/login'
      observer.error(error)
    }
  } else {
    // Other errors - pass through
    observer.error(error)
  }
}

const errorLink = onError(({ graphQLErrors, networkError, operation }: any) => {
  const operationName = operation.operationName as string | undefined
  const isReportOperation = operationName
    ? REPORT_OPERATION_NAMES.has(operationName)
    : false

  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path, extensions }: any) => {
      // Log ALL errors including auth errors for debugging
      errorLinkLogger.error('GraphQL error:', {
        message,
        locations,
        path,
        operation: operationName,
        errorCode: extensions?.code,
        originalError: extensions?.originalError,
        fullExtensions: JSON.stringify(extensions, null, 2),
      })

      // errorReporter: 인증 관련 코드(authErrorLink/refreshTokenLink가 처리)와
      // 리포트 요청 자체는 제외해 순환·노이즈를 막는다.
      const code = (extensions?.code as string | undefined) ?? ''
      const originalCode =
        (extensions?.originalError?.code as string | undefined) ?? ''
      if (
        isReportOperation ||
        AUTH_ERROR_CODES.has(code) ||
        AUTH_ERROR_CODES.has(originalCode)
      ) {
        return
      }

      // GraphQL 에러는 일반 Error 가 아니므로 new Error(message) 로 감싸면 extensions/
      // path/locations 가 유실된다. 디버깅에 유용한 정보를 extra 로 함께 전달.
      errorReporter.capture(new Error(message), {
        source: 'GRAPHQL',
        operationName,
        errorCode: code || originalCode || undefined,
        httpStatus:
          (extensions?.originalError?.statusCode as number | undefined) ??
          undefined,
        extra: {
          path,
          locations,
          extensions,
        },
      })
    })
  }

  if (networkError) {
    // Log ALL network errors including 401 for debugging
    errorLinkLogger.error('Network error:', {
      networkError: networkError.message,
      statusCode: (networkError as any).statusCode,
      operation: operationName,
      fullError: JSON.stringify(networkError, null, 2),
    })

    // 401/403은 authErrorLink/refreshTokenLink가 정상 플로우로 처리하므로 리포트 제외.
    // report mutation 자체 에러도 순환 방지 차원에서 제외.
    const httpStatus = (networkError as any).statusCode as number | undefined
    const isAuthNetworkError = httpStatus === 401 || httpStatus === 403
    if (!isReportOperation && !isAuthNetworkError) {
      errorReporter.capture(networkError, {
        source: 'NETWORK',
        operationName,
        httpStatus,
      })
    }
  }
})

export const apolloClient = new ApolloClient({
  link: ApolloLink.from([
    authErrorLink,
    refreshTokenLink,
    errorLink,
    authLink,
    httpLink,
  ]),
  cache: new InMemoryCache({
    typePolicies: {
      Menu: {
        fields: {
          children: {
            // children 필드는 항상 서버에서 받은 값으로 완전히 교체
            merge(_, incoming) {
              return incoming
            },
          },
        },
      },
      Query: {
        fields: {
          allMenus: {
            // allMenus 쿼리 결과도 항상 새로운 값으로 교체
            merge(_, incoming) {
              return incoming
            },
          },
        },
      },
    },
  }),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    },
    query: {
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    },
  },
})
